import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import { API } from "@/config/api";

const Contact = () => {
  const { t } = useLanguage();
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Scroll to form/success message when submitted
  useEffect(() => {
    if (submitted && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData, {
        withCredentials: true,
      });
      toast.success(response.data.message);
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <SEO 
        title="Contact"
        description="Contactez Byrōn Properties pour vos projets immobiliers à Genève. Notre équipe d'experts est à votre disposition pour vous accompagner."
        canonical="/contact"
      />
      {/* Hero Section - Clean image aligned with content grid */}
      <section className="relative bg-byron-bg">
        {/* Header spacer - accounts for fixed navigation */}
        <div className="header-spacer bg-byron-bg" />
        
        {/* Hero image - contained within content grid */}
        <div className="container-byron">
          <div className="relative h-[35vh] min-h-[280px] max-h-[380px] overflow-hidden media-frame">
            <img
              src="/maison-maitre.png"
              alt="Byron Properties"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Text Section - Below image */}
      <section className="py-8 md:py-12 bg-byron-bg">
        <div className="container-byron">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-xl"
          >
            <p className="label-text mb-3">
              {t('contact.heroSubtitle')}
            </p>
            <h1 className="heading-primary mb-5">
              {t('contact.heroTitle')}
            </h1>
            <p className="body-text">
              {t('contact.heroDescription')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content - Left-aligned grid */}
      <section className="py-10 md:py-14 bg-byron-bg">
        <div className="container-byron">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Contact Info - Left column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-4"
            >
              <div className="space-y-8">
                <div>
                  <p className="label-text mb-2">{t('contact.visitUs')}</p>
                  <p className="body-text">Chemin de Byron 12</p>
                  <p className="body-text">1223 Cologny, Geneva</p>
                  <p className="body-text">Switzerland</p>
                </div>

                <div>
                  <p className="label-text mb-2">{t('contact.callUs')}</p>
                  <a href="tel:+41227001234" className="body-text hover:text-[#8B7355] transition-colors">
                    +41 22 700 12 34
                  </a>
                </div>

                <div>
                  <p className="label-text mb-2">{t('contact.emailUs')}</p>
                  <a href="mailto:contact@byron-properties.ch" className="body-text hover:text-[#8B7355] transition-colors">
                    contact@byron-properties.ch
                  </a>
                </div>

                <div>
                  <p className="label-text mb-2">{t('contact.hours')}</p>
                  <p className="body-text">{t('contact.weekdays')}: 9:00 – 18:00</p>
                  <p className="body-text text-[#888] text-sm mt-1">{t('contact.weekend')}</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form - Right column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-7 lg:col-start-6"
            >
              {submitted ? (
                <div ref={formRef} className="bg-white p-12">
                  <div className="w-16 h-16 border border-[#C6A87C] rounded-full flex items-center justify-center mb-6">
                    <Check size={32} className="text-[#C6A87C]" />
                  </div>
                  <h3 className="heading-secondary mb-4">{t('contact.thankYou')}</h3>
                  <p className="body-text mb-6 max-w-sm">{t('contact.wellBeInTouch')}</p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ first_name: "", last_name: "", email: "", phone: "", message: "" });
                    }}
                    className="text-sm uppercase tracking-[0.15em] text-[#1A1A1A] hover:text-[#C6A87C] transition-colors inline-flex items-center gap-2"
                  >
                    {t('contact.sendMessage')}
                    <ArrowRight size={14} />
                  </button>
                </div>
              ) : (
                <div className="bg-white p-8 md:p-12">
                  <h2 className="text-xs uppercase tracking-[0.15em] text-[#666660] mb-8">{t('contact.sendMessage')}</h2>
                  
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs uppercase tracking-[0.1em] text-[#666660] mb-2">
                            {t('contact.firstName')}
                          </label>
                          <input
                            type="text"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#E5E5E0] py-3 focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="contact-first-name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs uppercase tracking-[0.1em] text-[#666660] mb-2">
                            {t('contact.lastName')}
                          </label>
                          <input
                            type="text"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#E5E5E0] py-3 focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="contact-last-name"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs uppercase tracking-[0.1em] text-[#666660] mb-2">
                            {t('contact.email')}
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#E5E5E0] py-3 focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="contact-email"
                          />
                        </div>
                        <div>
                          <label className="block text-xs uppercase tracking-[0.1em] text-[#666660] mb-2">
                            {t('contact.phone')}
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            required
                            className="w-full bg-transparent border-b border-[#E5E5E0] py-3 focus:border-[#1A1A1A] focus:outline-none"
                            data-testid="contact-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.1em] text-[#666660] mb-2">
                          {t('contact.message')}
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          rows={6}
                          className="w-full bg-byron-bg border border-[#E5E5E0] p-4 focus:border-[#1A1A1A] focus:outline-none resize-none"
                          data-testid="contact-message"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#1A1A1A] text-white py-4 px-8 text-xs uppercase tracking-[0.15em] hover:bg-[#333] transition-colors flex items-center gap-2"
                        data-testid="contact-submit"
                      >
                        {submitting ? t('contact.sending') : t('contact.send')}
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
